import { Color, Spacing } from '@persona-id/design-tokens';
import { AiFillIcon } from '@persona-id/design-tokens/icons';
import EN_US from 'locales/translation.en-US.json';
import { intersection } from 'lodash/fp';
import { useState } from 'react';
import documentClassificationsJson from 'shared/documents/document_classifications.json';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  Classification,
  ClassificationMethod,
  DefinitionType,
  ToggleOption,
} from 'dashboard/models/document';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import Stack from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const COUNTRIES_MAPPING: Record<string, string> = EN_US.countries;
const COUNTRIES_OPTIONS = Object.entries(COUNTRIES_MAPPING).map(([value, label]) => ({
  value,
  label,
}));
const COUNTRIES_MAX_DISPLAY = 3;

const CountryTooltipContent = styled.div`
  max-width: 256px;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[1]};
  justify-content: center;
`;

export const SupportedCountries = ({
  supportedCountryCodes,
}: {
  supportedCountryCodes: string[];
}) => {
  const length = supportedCountryCodes.length;

  return (
    <span style={{ display: 'flex', columnGap: Spacing[1] }}>
      {length === 0
        ? 'All supported countries and regions'
        : supportedCountryCodes
            .slice(0, COUNTRIES_MAX_DISPLAY)
            .map((countryCode) => (
              <Sticker key={countryCode}>{COUNTRIES_MAPPING[countryCode]}</Sticker>
            ))}
      {length > COUNTRIES_MAX_DISPLAY && (
        <Tooltip
          side='right'
          id='classifications-preset-modal__supported-country-tooltip'
          zIndex={Z_INDICES.modalElevated}
          trigger={<Paragraph>{`+ ${length - COUNTRIES_MAX_DISPLAY} more`}</Paragraph>}
          onClick={(e) => e.stopPropagation()}
        >
          <CountryTooltipContent>
            {supportedCountryCodes.slice(COUNTRIES_MAX_DISPLAY).map((countryCode) => (
              <Sticker key={countryCode}>{COUNTRIES_MAPPING[countryCode]}</Sticker>
            ))}
          </CountryTooltipContent>
        </Tooltip>
      )}
    </span>
  );
};

const FilterContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing[2]};
`;

const TableContainer = styled.div`
  height: 512px;
  overflow-y: auto;
`;

const Footer = styled.div`
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: ${Spacing[2]};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
`;

// TODO: [Documents] Migrate to PDS component when available
const UnderlinedTooltipText = styled.span`
  text-decoration: underline;
  text-decoration-style: dotted;
`;

const TooltipContent = styled(Paragraph)`
  max-width: 400px;
`;

const StyledParagraph = styled(Paragraph)`
  margin-top: ${Spacing[2]};
  color: ${Color.TextDanger};
`;

const compareClassifications = (
  classification1: Classification,
  classification2: Classification,
) => {
  return (
    classification1.key === classification2.key &&
    classification1['definition-type'] === classification2['definition-type'] &&
    classification1['classification-method'] === classification2['classification-method']
  );
};

interface ClassificationsConfigProps {
  classifications: Classification[];
  open: boolean;
  handleCloseModal: () => void;
  onConfirm: (classifications: Classification[]) => void;
}

const ClassificationsPresetModal = ({
  classifications,
  open,
  handleCloseModal,
  onConfirm,
}: ClassificationsConfigProps) => {
  const [selected, setSelected] = useState<Classification[]>(classifications);

  const [searchString, setSearchString] = useState('');
  const [searchCountries, setSearchCountries] = useState<string[]>([]);

  const classificationIsSelected = (classification: Classification) => {
    return selected.some((item) => compareClassifications(item, classification));
  };

  const handleSelect = (classification: Classification) => {
    if (!classificationIsSelected(classification)) {
      setSelected([...selected, classification]);
    } else {
      setSelected(selected.filter((item) => !compareClassifications(item, classification)));
    }
  };

  const handleConfirm = () => {
    onConfirm(selected);
    handleCloseModal();
  };

  const disabledClassificationKeys = selected.map(
    (classificationIdentifier) => classificationIdentifier.key,
  );

  const filteredClassifications = documentClassificationsJson.filter(
    (documentClassificationJson) =>
      documentClassificationJson.definition_type === DefinitionType.Preset &&
      documentClassificationJson.label.toLowerCase().includes(searchString.toLowerCase()) &&
      (searchCountries.length === 0 ||
        intersection(documentClassificationJson.supported_country_codes, searchCountries).length >
          0),
  );

  const isPersonerd = useIsPersonerd();
  const showClassificationsAiTagFlag = useFeatureFlag('dashboard:show-classifications-ai-tag');
  const showClassificationsAiTagPermission = usePermission(
    Permission.VerificationDocumentClassificationsAiTagView,
  );
  const showClassificationsAiTag =
    showClassificationsAiTagFlag && showClassificationsAiTagPermission;

  return (
    <ModalWrapper isOpen={open} handleCloseModal={handleCloseModal}>
      <ModalContainer size='xl'>
        <ModalHeader onClose={handleCloseModal}>
          Persona document types
          <Paragraph>
            Select a document type to add under Classifications. Duplicate document types with
            different supported countries cannot be added.
          </Paragraph>
        </ModalHeader>
        <Spacer height={1} />
        <ModalBody>
          <FilterContainer>
            <SearchInput
              title={null}
              placeholder='Search document types'
              value={searchString}
              handleInput={setSearchString}
            />
            <MultiSelect
              value={searchCountries}
              selectOptionValues={COUNTRIES_OPTIONS}
              labelTextMapper={useLabelTextMapper(COUNTRIES_OPTIONS, 'countries', {
                emptyIsAll: true,
              })}
              onChange={setSearchCountries}
              searchEnabled
            />
          </FilterContainer>
          <Spacer height={1} />
          <TableContainer>
            <Table>
              <TableHead sticky>
                <TableRow>
                  <TableHeaderCell width='min' />
                  <TableHeaderCell>Document type</TableHeaderCell>
                  <TableHeaderCell>Supported countries</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClassifications.map((classificationJson, index) => {
                  const classification: Classification = {
                    key: classificationJson.key,
                    'definition-type': classificationJson.definition_type as DefinitionType,
                    'classification-method':
                      classificationJson.classification_method as ClassificationMethod,
                    label: classificationJson.label,
                    description: classificationJson.description,
                    'toggle-option': ToggleOption.Enabled,
                  };

                  const checked = classificationIsSelected(classification);
                  const disabled =
                    !checked && disabledClassificationKeys.includes(classification.key);

                  return (
                    <TableRow
                      key={`${classificationJson.key}-${classificationJson.definition_type}-${classificationJson.classification_method}`}
                      id={`classifications-preset-modal__row-${index}`}
                      onClick={!disabled ? () => handleSelect(classification) : undefined}
                    >
                      <TableCell width='min'>
                        <Checkbox checked={checked} disabled={disabled} />
                      </TableCell>
                      <TableCell>
                        <div>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip
                              side='right'
                              zIndex={Z_INDICES.modalElevated}
                              trigger={
                                <UnderlinedTooltipText color='primary'>
                                  {classificationJson.label}
                                </UnderlinedTooltipText>
                              }
                              // Prevent click-through via tooltip to table row
                              id={`classifications-preset-modal__row-${index}-tooltip`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TooltipContent>{classificationJson.description}</TooltipContent>
                            </Tooltip>
                            {classificationJson.classification_method === ClassificationMethod.Ai &&
                              (isPersonerd || showClassificationsAiTag) && (
                                <>
                                  <Spacer height={0} width={1} />
                                  <Sticker variant='purple' icon={<AiFillIcon />}>
                                    AI
                                  </Sticker>
                                </>
                              )}
                          </span>
                        </div>
                        {disabled && (
                          <div>
                            <StyledParagraph size='sm' style={{ lineHeight: 1 }}>
                              Conflict: Remove duplicate {classification.label} to add.
                            </StyledParagraph>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <SupportedCountries
                          supportedCountryCodes={classificationJson.supported_country_codes}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Footer>
            <Stack direction='horizontal'>
              <Button
                id='classifications-preset-modal__close-button'
                variant='basic'
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
              <Button id='classifications-preset-modal__confirm-button' onClick={handleConfirm}>
                Confirm ({selected.length})
              </Button>
            </Stack>
          </Footer>
        </ModalBody>
      </ModalContainer>
    </ModalWrapper>
  );
};

export default ClassificationsPresetModal;
